import { shortenNumber } from '@yes.technology/react-toolkit'
import { statesWithOverlappingLabels } from 'site/Renderer/components/FilterableMap/utils/statesWithOverlappingLabels'
import { getContrastColor } from '../../utils/color'
import { statesByRegion } from '../../utils/regionsMapUtils'
import { ApplyColorsProps, SetLabelsProps } from './BrazilMap.types'

export function applyColors({
  coloredData,
  defaultColor,
  isRegionMap,
  isStateMap,
  svgElement
}: ApplyColorsProps) {
  if (!svgElement.current) return

  const paths = svgElement.current.querySelectorAll('path')

  paths.forEach((path) => {
    const stateId = path.getAttribute('id')

    let backgroundColor = defaultColor
    const strokeColor = '#ccc'

    if (isStateMap) {
      backgroundColor =
        coloredData.find((p) => p.region === stateId)?.color || defaultColor
    }

    if (isRegionMap && stateId) {
      const region = Object.entries(statesByRegion).find(([, states]) =>
        states.includes(stateId)
      )?.[0]

      backgroundColor =
        coloredData.find((p) => p.region === region)?.color || defaultColor
    }

    path.setAttribute('fill', backgroundColor)
    path.setAttribute('stroke', strokeColor)
  })
}

export function setLabels({
  labelSelector,
  data,
  svgElement,
  coloredData,
  defaultBackgroundColor = '#F1F1F1',
  format,
  locale = 'en-US',
  idTransformer = (id: string) => id
}: SetLabelsProps) {
  if (!svgElement.current) return

  const svg = svgElement.current

  // Adiciona o filtro de sombra ao SVG
  const defs =
    svg.querySelector('defs') ||
    document.createElementNS('http://www.w3.org/2000/svg', 'defs')
  if (!svg.querySelector('defs')) svg.appendChild(defs)

  const filter = document.createElementNS(
    'http://www.w3.org/2000/svg',
    'filter'
  )
  filter.setAttribute('id', 'textShadow')
  filter.setAttribute('x', '-50%')
  filter.setAttribute('y', '-50%')
  filter.setAttribute('width', '200%')
  filter.setAttribute('height', '200%')

  const feDropShadow = document.createElementNS(
    'http://www.w3.org/2000/svg',
    'feDropShadow'
  )
  feDropShadow.setAttribute('dx', '1') // Deslocamento horizontal da sombra
  feDropShadow.setAttribute('dy', '1') // Deslocamento vertical da sombra
  feDropShadow.setAttribute('stdDeviation', '2') // Intensidade da sombra (desfoque)
  feDropShadow.setAttribute('flood-color', 'rgba(0, 0, 0, 0.5)')

  filter.appendChild(feDropShadow)
  defs.appendChild(filter)

  const circles = svg.querySelectorAll(labelSelector + ' circle')

  circles.forEach((circle) => {
    const id = circle.getAttribute('id') || ''
    const label = data.find((p) => p[0] === id)?.[1]

    const isLabel = label !== undefined && label !== null && label !== ''

    const isStateWithLabelOverlapping = Object.keys(
      statesWithOverlappingLabels
    ).includes(id)

    if (isLabel) {
      let cx = circle.getAttribute('cx') || '0'
      let cy = circle.getAttribute('cy') || '0'

      const initialXStatePosition = cx // Posição inicial da linha de ligação no eixo X
      const initialYStatePosition = cy // Posição inicial da linha de ligação no eixo Y

      if (isStateWithLabelOverlapping) {
        const { offsetX, offsetY } = statesWithOverlappingLabels[id]

        cx = `${Number(cx) + offsetX}` // Posição final da linha de ligação no eixo X
        cy = `${Number(cy) + offsetY}` // Posição final da linha de ligação no eixo Y

        // Adiciona linha de ligação entre o estado e o label
        const lineLabelSvg = document.createElementNS(
          'http://www.w3.org/2000/svg',
          'svg'
        )

        lineLabelSvg.setAttribute('width', '1000')
        lineLabelSvg.setAttribute('height', '1000')

        const line = document.createElementNS(
          'http://www.w3.org/2000/svg',
          'line'
        )

        line.setAttribute('x1', initialXStatePosition)
        line.setAttribute('y1', initialYStatePosition)

        line.setAttribute('x2', cx)
        line.setAttribute('y2', cy)

        line.setAttribute('stroke', '#ccc')
        line.setAttribute('stroke-width', '1')

        lineLabelSvg.appendChild(line)

        svg?.appendChild(lineLabelSvg)
      }

      const backgroundColor =
        coloredData.find((p) => p.region === id)?.color ||
        defaultBackgroundColor

      const labelColor = isStateWithLabelOverlapping
        ? '#000'
        : getContrastColor(backgroundColor)

      const text = document.createElementNS(
        'http://www.w3.org/2000/svg',
        'text'
      )
      text.setAttribute('x', cx)
      text.setAttribute('y', cy)
      text.setAttribute('font-size', '1.5rem')
      text.setAttribute('text-anchor', 'middle')
      text.setAttribute('fill', labelColor)
      text.setAttribute('stroke', 'none')
      text.setAttribute('id', idTransformer(id).toLowerCase())

      if (labelColor === '#ffffff' && !isStateWithLabelOverlapping) {
        text.setAttribute('filter', 'url(#textShadow)')
      }

      const tspanName = document.createElementNS(
        'http://www.w3.org/2000/svg',
        'tspan'
      )
      tspanName.textContent = idTransformer(id)
      tspanName.setAttribute('x', cx)
      tspanName.setAttribute('dy', '0')

      const tspanLabel = document.createElementNS(
        'http://www.w3.org/2000/svg',
        'tspan'
      )

      tspanLabel.textContent = label.toString()

      if (format) {
        tspanLabel.textContent = setFormat(label, format, locale)
      }

      tspanLabel.setAttribute('x', cx)
      tspanLabel.setAttribute('dy', '1em')

      text.appendChild(tspanName)
      text.appendChild(tspanLabel)

      svg?.appendChild(text)
    }
  })
}

export function setFormat(
  value: number | string | null,
  format: string,
  locale = 'en-US'
): string {
  if (format === 'short') {
    return shortenNumber(Number(value))
  } else if (format === 'decimal') {
    return Intl.NumberFormat(locale).format(Number(value))
  }
  return value?.toString() || ''
}
